import { useState, FormEvent } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { checkAuth, setPassword } from "../../services/api";
import { Layout } from "../Layout";

export function SetPasswordForm({
  isReset,
  setIsAuthenticated,
  setUser,
}: {
  isReset: boolean;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: { id: string; email: string }) => void;
}) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [password, setPasswordValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!token) {
    return <Navigate to="/signup" replace />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSuccessMessage(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const data = await setPassword({ token, password });
      localStorage.setItem("authToken", data.token);
      const { user } = await checkAuth();
      setIsAuthenticated(true);
      setUser(user!);
      if (isReset) {
        navigate("/login", { state: { message: "Password reset successful" } });
      } else {
        navigate("/subscription");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="row flex-grow-1 justify-content-center">
        <div className="col d-flex" style={{ position: "relative" }}>
          <img
            src="/birdie_logo.svg"
            alt="Company Logo"
            style={{ height: "40px", position: "absolute", top: "0" }}
          />
          <div>
            <div
              className="font-size-large font-weight-bold"
              style={{ marginTop: "120px" }}
            >
              {isReset ? "Reset your password" : "Finish creating your account"}
            </div>
            <div>
              {isReset ? "Enter your new password" : "Enter your password"}
            </div>
          </div>
        </div>
        <div className="col d-flex">
          <form
            className="flex-grow-1 d-flex flex-column justify-content-between"
            onSubmit={handleSubmit}
          >
            <div style={{ marginTop: "calc(120px - 0.5rem" }}>
              <input
                type="password"
                className={`form-control ${error ? "is-invalid" : ""}`}
                placeholder="Password"
                value={password}
                onChange={(e) => setPasswordValue(e.target.value)}
                disabled={isLoading}
                required
                minLength={8}
              />
              <input
                type="password"
                className={`form-control ${error ? "is-invalid" : ""} mt-2`}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="alert alert-success py-2" role="alert">
                {successMessage}
              </div>
            )}
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={successMessage !== null}
              >
                {isLoading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                {isReset ? "Set Password" : "Create Account"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
